import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard,
  TrendingUp,
  ShoppingBag,
  Calendar,
  Package,
  ArrowRight,
  Euro,
  Clock,
  ArrowLeft,
  Check,
  Circle,
  Rocket,
} from 'lucide-react';
import api from '../utils/api';

const formatEUR = (n) =>
  new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(n || 0);

// ============= ONBOARDING CHECKLIST =============
const OnboardingChecklist = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/reseller/onboarding-status');
        setStatus(res.data);
        // Auto-collapse when 100% done
        if (res.data.percent >= 100) setCollapsed(true);
      } catch {
        setStatus(null);
      }
    })();
  }, []);

  if (!status) return null;
  const { steps, done_count, total, percent } = status;
  const allDone = done_count >= total;

  return (
    <div
      className={`mb-6 rounded-xl border ${allDone ? 'bg-green-50 border-green-200' : 'bg-gradient-to-r from-primary/5 to-amber-50 border-amber-200'} p-5`}
      data-testid="onboarding-checklist"
    >
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="flex items-center gap-3">
          <Rocket className={`h-6 w-6 ${allDone ? 'text-green-700' : 'text-primary'}`} />
          <div>
            <h2 className="font-semibold text-lg">
              {allDone ? 'Ihr Shop ist vollständig eingerichtet' : 'Shop-Einrichtung'}
            </h2>
            <p className="text-sm text-gray-600" data-testid="onboarding-progress-text">
              {done_count}/{total} Schritte abgeschlossen ({percent}%)
            </p>
          </div>
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-sm text-gray-500 hover:text-gray-700 whitespace-nowrap"
          data-testid="onboarding-toggle"
        >
          {collapsed ? 'Aufklappen' : 'Zuklappen'}
        </button>
      </div>
      {/* Progress bar */}
      <div className="w-full h-2 bg-white rounded-full overflow-hidden mb-4">
        <div
          className={`h-full rounded-full transition-all ${allDone ? 'bg-green-600' : 'bg-primary'}`}
          style={{ width: `${percent}%` }}
          data-testid="onboarding-progress-bar"
        />
      </div>
      {!collapsed && (
        <ul className="space-y-2">
          {steps.map((s) => (
            <li key={s.key} className="flex items-center justify-between gap-3 py-1" data-testid={`onboarding-step-${s.key}`}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {s.done ? (
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                ) : (
                  <Circle className="h-5 w-5 text-gray-300 flex-shrink-0" />
                )}
                <span className={`text-sm ${s.done ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                  {s.label}
                </span>
              </div>
              {!s.done && s.route && (
                <button
                  onClick={() => navigate(s.route)}
                  className="text-xs text-primary hover:underline whitespace-nowrap font-medium"
                  data-testid={`onboarding-goto-${s.key}`}
                >
                  Erledigen →
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const MyShopDashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/reseller/dashboard/stats')
      .then((res) => setStats(res.data))
      .catch((e) => console.error('Dashboard stats failed', e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-500">
          Keine Daten verfügbar. Bitte richten Sie zuerst Ihren Shop ein.
        </p>
        <Button onClick={() => navigate('/my-shop')} className="mt-4">
          Zum Shop
        </Button>
      </div>
    );
  }

  const revenue = stats.revenue;
  const ordersInfo = stats.orders;
  const apptInfo = stats.appointments;
  const topProducts = stats.top_products || [];
  const recentOrders = ordersInfo.recent || [];
  const upcomingAppts = apptInfo.upcoming || [];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="my-shop-dashboard">
      <div className="mb-6">
        <button
          onClick={() => navigate('/my-shop')}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück zum Shop
        </button>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <LayoutDashboard className="h-7 w-7 text-primary" />
          Dashboard
        </h1>
        <p className="text-gray-500 mt-1">Ihre Shop-Übersicht auf einen Blick</p>
      </div>

      <OnboardingChecklist />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-border rounded-lg p-5" data-testid="stat-revenue-today">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Umsatz heute</p>
              <p className="text-2xl font-bold mt-1 text-primary">{formatEUR(revenue.today)}</p>
            </div>
            <div className="p-2 rounded-lg bg-primary/10">
              <Euro className="h-5 w-5 text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-lg p-5" data-testid="stat-revenue-week">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Diese Woche</p>
              <p className="text-2xl font-bold mt-1 text-primary">{formatEUR(revenue.week)}</p>
            </div>
            <div className="p-2 rounded-lg bg-primary/10">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-lg p-5" data-testid="stat-revenue-month">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Dieser Monat</p>
              <p className="text-2xl font-bold mt-1 text-primary">{formatEUR(revenue.month)}</p>
            </div>
            <div className="p-2 rounded-lg bg-primary/10">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-lg p-5" data-testid="stat-revenue-total">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Gesamt</p>
              <p className="text-2xl font-bold mt-1 text-primary">{formatEUR(revenue.total)}</p>
              <p className="text-xs text-gray-400 mt-1">{ordersInfo.total} Bestellungen</p>
            </div>
            <div className="p-2 rounded-lg bg-primary/10">
              <Euro className="h-5 w-5 text-primary" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-border rounded-lg p-5" data-testid="stat-new-orders">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Neue Bestellungen</p>
              <p className="text-2xl font-bold mt-1 text-primary">{ordersInfo.new_count}</p>
              <p className="text-xs text-gray-400 mt-1">warten auf Bearbeitung</p>
            </div>
            <div className="p-2 rounded-lg bg-primary/10">
              <ShoppingBag className="h-5 w-5 text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-lg p-5" data-testid="stat-appointments-today">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Termine heute</p>
              <p className="text-2xl font-bold mt-1 text-primary">{apptInfo.today_count}</p>
              <p className="text-xs text-gray-400 mt-1">anstehend</p>
            </div>
            <div className="p-2 rounded-lg bg-primary/10">
              <Clock className="h-5 w-5 text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-lg p-5" data-testid="stat-appointments-week">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Nächste 7 Tage</p>
              <p className="text-2xl font-bold mt-1 text-primary">{apptInfo.upcoming_count}</p>
              <p className="text-xs text-gray-400 mt-1">Termine geplant</p>
            </div>
            <div className="p-2 rounded-lg bg-primary/10">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-border rounded-lg p-6" data-testid="recent-orders-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-primary" />
              Neue Bestellungen
            </h2>
            <button
              onClick={() => navigate('/my-shop/orders')}
              className="text-sm text-primary hover:underline flex items-center gap-1"
              data-testid="view-all-orders"
            >
              Alle anzeigen <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          {recentOrders.length === 0 && (
            <p className="text-sm text-gray-500 py-8 text-center">Keine neuen Bestellungen</p>
          )}
          {recentOrders.length > 0 && (
            <div className="space-y-3">
              {recentOrders.map((o) => (
                <div
                  key={o.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => navigate('/my-shop/orders')}
                >
                  <div>
                    <p className="font-medium text-sm">#{o.order_number}</p>
                    <p className="text-xs text-gray-500">
                      {o.customer_first_name} {o.customer_last_name}
                    </p>
                  </div>
                  <div className="text-end">
                    <p className="font-semibold text-primary">{formatEUR(o.total_gross)}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(o.created_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-border rounded-lg p-6" data-testid="upcoming-appointments-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Anstehende Termine
            </h2>
            <button
              onClick={() => navigate('/my-shop/appointments')}
              className="text-sm text-primary hover:underline flex items-center gap-1"
              data-testid="view-all-appointments"
            >
              Alle anzeigen <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          {upcomingAppts.length === 0 && (
            <p className="text-sm text-gray-500 py-8 text-center">Keine anstehenden Termine</p>
          )}
          {upcomingAppts.length > 0 && (
            <div className="space-y-3">
              {upcomingAppts.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => navigate('/my-shop/appointments')}
                >
                  <div>
                    <p className="font-medium text-sm">{a.customer_name}</p>
                    <p className="text-xs text-gray-500">{a.service_name}</p>
                  </div>
                  <div className="text-end">
                    <p className="font-semibold text-sm">{a.appointment_date}</p>
                    <p className="text-xs text-primary">{a.appointment_time} Uhr</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg p-6" data-testid="top-products-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Top 5 Produkte
          </h2>
          <button
            onClick={() => navigate('/my-shop/products')}
            className="text-sm text-primary hover:underline flex items-center gap-1"
            data-testid="view-all-products"
          >
            Alle Produkte <ArrowRight className="h-3 w-3" />
          </button>
        </div>
        {topProducts.length === 0 && (
          <p className="text-sm text-gray-500 py-8 text-center">Noch keine verkauften Produkte</p>
        )}
        {topProducts.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-xs text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="text-start py-2">Produkt</th>
                  <th className="text-end py-2">Verkauft</th>
                  <th className="text-end py-2">Umsatz</th>
                </tr>
              </thead>
              <tbody>
                {topProducts.map((p, i) => (
                  <tr key={p.product_id} className="border-t border-gray-100">
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">
                          {i + 1}
                        </span>
                        <span className="text-sm">{p.name}</span>
                      </div>
                    </td>
                    <td className="py-3 text-end text-sm font-medium">{p.quantity}x</td>
                    <td className="py-3 text-end text-sm font-semibold text-primary">
                      {formatEUR(p.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyShopDashboardPage;
